if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      constructor() {
        super();

        this.input = this.querySelector('.quantity__input');

        this.currentVariant = this.querySelector('variant-selects [data-selected-variant]')?.innerHTML
          ? JSON.parse(this.querySelector('variant-selects [data-selected-variant]').innerHTML)
          : null;

        this.productForm = this.querySelector('product-form');
        this.submitButton = this.querySelector('[type="submit"][name="add"]');
        this.submitButtonText = this.submitButton?.querySelector('span');

        this.abortController = null;
        this.pendingRequestUrl = null;
        this.unsubscribeFromVariantChange = null;
      }

      connectedCallback() {
        this.quantityForm = this.querySelector('.product-form__quantity');

        if (!this.dataset.originalSection) {
          this.dataset.originalSection = this.dataset.section;
        }

        /*
          Critical variant fix:
          Listen for Shopify variant option changes.
          Without this, handleUpdateProductInfo may never run when customers select options.
        */
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeFromVariantChange = subscribe(
            PUB_SUB_EVENTS.optionValueSelectionChange,
            this.handleUpdateProductInfo.bind(this)
          );
        }

        this.forceSelectedVariantBeforeSubmit();

        if (!this.input) return;

        this.setQuantityBoundries();
      }

      disconnectedCallback() {
        if (this.abortController) {
          this.abortController.abort();
        }

        if (this.unsubscribeFromVariantChange) {
          this.unsubscribeFromVariantChange();
        }
      }

      /**
       * Called by Shopify's variant-selects component when an option changes.
       * Avoids injecting sold-out messaging and updates the cart form variant ID.
       */
      handleUpdateProductInfo({ data: { event, target, selectedOptionValues } }) {
        if (!this.dataset.url) return;

        this.abortController?.abort();
        this.abortController = new AbortController();

        const requestedVariantId = target?.selectedOptions?.[0]?.dataset?.optionValueId;
        const sectionId = this.dataset.originalSection || this.dataset.section;
        const productUrl = target?.dataset?.productUrl || this.dataset.url;

        this.pendingRequestUrl = productUrl;

        const params = new URLSearchParams({
          section_id: sectionId,
        });

        if (selectedOptionValues && selectedOptionValues.length) {
          params.set('option_values', selectedOptionValues.join(','));
        } else if (requestedVariantId) {
          params.set('variant', requestedVariantId);
        }

        const requestUrl = `${productUrl}?${params.toString()}`;

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            if (this.pendingRequestUrl !== productUrl) return;

            const html = new DOMParser().parseFromString(responseText, 'text/html');

            const destination = document.getElementById(`ProductInfo-${this.dataset.section}`);
            const source = html.getElementById(`ProductInfo-${sectionId}`);

            if (!source || !destination) return;

            this.currentVariant = this.getSelectedVariant(html);

            this.updateURL(productUrl, this.currentVariant?.id);
            this.updateVariantInputs(this.currentVariant?.id);
            this.updateMedia(html, this.currentVariant?.featured_media?.id);
            this.updateProductInfoBlocks(source, destination);
            this.updateQuantityRules(source, destination);
            this.setQuantityBoundries();
            this.removeCrawlableAvailabilityNoise();

            if (this.currentVariant) {
              this.enableSubmitButton();
            } else {
              this.disableSubmitButtonWithoutSoldOutText();
            }

            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.variantChange, {
                data: {
                  sectionId: this.dataset.section,
                  html,
                  variant: this.currentVariant,
                },
              });
            }
          })
          .catch((error) => {
            if (error.name === 'AbortError') return;
            console.error(error);
          });
      }

      getSelectedVariant(html) {
        const selectedVariant = html.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      updateURL(productUrl, variantId) {
        if (!this.dataset.updateUrl || this.dataset.updateUrl === 'false') return;
        if (!variantId) return;

        window.history.replaceState({}, '', `${productUrl}?variant=${variantId}`);
      }

      /**
       * Updates the hidden Shopify variant ID input used by Add to Cart.
       * This is the key field Shopify submits to cart.
       */
      updateVariantInputs(variantId) {
        if (!variantId) return;

        const sectionId = this.dataset.section;
        const originalSectionId = this.dataset.originalSection || sectionId;

        const productForms = document.querySelectorAll(
          `#product-form-${sectionId}, #product-form-${originalSectionId}, #product-form-installment-${sectionId}, #product-form-installment-${originalSectionId}`
        );

        productForms.forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');

          if (!input) return;

          input.value = variantId;
          input.removeAttribute('disabled');
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      /**
       * Extra safety:
       * Before add-to-cart submits, force the latest selected variant ID
       * into the hidden name="id" input.
       */
      forceSelectedVariantBeforeSubmit() {
        const sectionId = this.dataset.section;
        const originalSectionId = this.dataset.originalSection || sectionId;

        const productForms = document.querySelectorAll(
          `#product-form-${sectionId}, #product-form-${originalSectionId}`
        );

        productForms.forEach((productForm) => {
          if (productForm.dataset.variantSubmitFixAttached === 'true') return;

          productForm.dataset.variantSubmitFixAttached = 'true';

          productForm.addEventListener('submit', () => {
            if (!this.currentVariant?.id) return;

            const input = productForm.querySelector('input[name="id"]');

            if (!input) return;

            input.value = this.currentVariant.id;
            input.removeAttribute('disabled');
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
        });
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = html.querySelector('media-gallery ul');
        const mediaGalleryDestination = this.querySelector('media-gallery ul');

        if (!mediaGallerySource || !mediaGalleryDestination) return;

        mediaGalleryDestination.innerHTML = mediaGallerySource.innerHTML;

        const mediaGallery = this.querySelector('media-gallery');

        if (mediaGallery?.setActiveMedia) {
          mediaGallery.setActiveMedia(`${this.dataset.section}-${variantFeaturedMediaId}`, true);
        }
      }

updateProductInfoBlocks(source, destination) {
  const sourceSectionId =
    this.dataset.originalSection ||
    this.dataset.section;

  const destinationSectionId =
    this.dataset.section;

  const blocks = [
    { prefix: 'price', lowercase: true },
    { prefix: 'Sku' },
    { prefix: 'Inventory' },
    { prefix: 'Quantity-Rules' },
    { prefix: 'Volume-Note' },
    { prefix: 'Volume' },
    { prefix: 'Price-Per-Item' },
  ];

  blocks.forEach(({ prefix }) => {
    const sourceBlock =
      source.querySelector(`#${prefix}-${sourceSectionId}`) ||
      source.querySelector(`#${prefix}-${destinationSectionId}`);

    const destinationBlock =
      destination.querySelector(`#${prefix}-${destinationSectionId}`) ||
      destination.querySelector(`#${prefix}-${sourceSectionId}`);

    if (!sourceBlock || !destinationBlock) {
      return;
    }

    destinationBlock.innerHTML = sourceBlock.innerHTML;
    destinationBlock.className = sourceBlock.className;
  });

  const price =
    destination.querySelector(`#price-${destinationSectionId}`) ||
    destination.querySelector(`#price-${sourceSectionId}`);

  if (price) {
    price.classList.remove('visibility-hidden');
  }

  const inventory =
    destination.querySelector(`#Inventory-${destinationSectionId}`) ||
    destination.querySelector(`#Inventory-${sourceSectionId}`);

  if (inventory) {
    inventory.classList.remove('visibility-hidden');
  }

  const sku =
    destination.querySelector(`#Sku-${destinationSectionId}`) ||
    destination.querySelector(`#Sku-${sourceSectionId}`);

  if (sku) {
    sku.classList.remove('visibility-hidden');
  }
}

updateQuantityRules(source, destination) {
  const sourceSectionId =
    this.dataset.originalSection ||
    this.dataset.section;

  const destinationSectionId =
    this.dataset.section;

  const sourceQuantityInput =
    source.querySelector(`#Quantity-${sourceSectionId}`) ||
    source.querySelector(`#Quantity-${destinationSectionId}`);

  const destinationQuantityInput =
    destination.querySelector(`#Quantity-${destinationSectionId}`) ||
    destination.querySelector(`#Quantity-${sourceSectionId}`);

  if (!sourceQuantityInput || !destinationQuantityInput) {
    return;
  }

  [
    'data-cart-quantity',
    'data-min',
    'min',
    'data-max',
    'max',
    'step',
    'value',
  ].forEach((attribute) => {
    if (sourceQuantityInput.hasAttribute(attribute)) {
      destinationQuantityInput.setAttribute(
        attribute,
        sourceQuantityInput.getAttribute(attribute)
      );
    } else {
      destinationQuantityInput.removeAttribute(attribute);
    }
  });

  this.input = destinationQuantityInput;
}

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.input?.dataset.cartQuantity ? parseInt(this.input.dataset.cartQuantity) : 0,
          min: this.input?.dataset.min ? parseInt(this.input.dataset.min) : 1,
          max: this.input?.dataset.max ? parseInt(this.input.dataset.max) : null,
          step: this.input?.step ? parseInt(this.input.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;

        if (max !== null) min = Math.min(min, max);

        if (!this.input) return;

        this.input.min = min;

        if (max) {
          this.input.max = max;
        } else {
          this.input.removeAttribute('max');
        }

        this.input.value = min;

        const quantityRules = this.querySelector('.quantity__rules');

        if (quantityRules) {
          quantityRules.classList.toggle('hidden', data.min === 1 && data.max === null && data.step === 1);
        }
      }

      enableSubmitButton() {
        const buttons = this.querySelectorAll('[type="submit"][name="add"]');

        buttons.forEach((button) => {
          button.removeAttribute('disabled');
          button.setAttribute('aria-disabled', 'false');

          const buttonText = button.querySelector('span');

          if (buttonText) {
            buttonText.textContent = window.variantStrings?.addToCart || 'Add to cart';
          }
        });

        this.querySelectorAll('.product-variant-id').forEach((input) => {
          input.removeAttribute('disabled');
        });

        const paymentButton = this.querySelector('.shopify-payment-button');

        if (paymentButton) paymentButton.classList.remove('hidden');
      }

      disableSubmitButtonWithoutSoldOutText() {
        const buttons = this.querySelectorAll('[type="submit"][name="add"]');

        buttons.forEach((button) => {
          button.setAttribute('disabled', 'disabled');
          button.setAttribute('aria-disabled', 'true');

          const buttonText = button.querySelector('span');

          if (buttonText) {
            buttonText.textContent = 'Select options';
          }
        });

        const paymentButton = this.querySelector('.shopify-payment-button');

        if (paymentButton) paymentButton.classList.add('hidden');
      }

      /**
       * Safety cleanup for Google Merchant Center:
       * removes hidden or dynamic sold-out text that some Ride/Dawn JS snippets expect.
       */
      removeCrawlableAvailabilityNoise() {
        this.querySelectorAll('.sold-out-message').forEach((element) => element.remove());

        this.querySelectorAll('[data-sold-out-message]').forEach((element) => {
          element.removeAttribute('data-sold-out-message');
        });

        this.querySelectorAll('.price__badge-sold-out').forEach((element) => {
          const priceWrapper = element.closest('.price');

          if (!priceWrapper?.classList.contains('price--sold-out')) {
            element.remove();
          }
        });

        this.querySelectorAll('.label-unavailable').forEach((element) => element.remove());

        this.querySelectorAll('.product-form__input input.disabled').forEach((input) => {
          input.classList.remove('disabled');
          input.removeAttribute('disabled');
        });

        this.querySelectorAll('.product-form__input option:disabled').forEach((option) => {
          option.removeAttribute('disabled');
        });
      }
    }
  );
}